import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  confirmForgotPasswordSuccess: false,
  myProfile: null,
  selectedUserProfile: null,
  users: [],
  userList: [],
  enabledUserList: [],
  cards: null,
  userPermissions: [],
  notifications: null,
  isResetPasswordError: false,
  isResetPasswordSuccess: false,
  resetPasswordTrigger: 0
};

const parsePermissions = (username, userPermissions) => {
  if (userPermissions.brands !== '') {
    userPermissions.brands = JSON.parse(userPermissions.brands);
  }
  if (userPermissions.branches !== '') {
    userPermissions.branches = JSON.parse(userPermissions.branches);
  }
  // userPermissions.role = JSON.parse(userPermissions.role);
  console.debug('parsing permissions to JSON object', userPermissions);
  userPermissions.username = username;
  return userPermissions;
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // GET PROFILE
    getProfileSuccess(state, action) {
      state.isLoading = false;
      state.myProfile = action.payload;
    },

    // GET PROFILE
    getAdminProfileSuccess(state, action) {
      state.isLoading = false;
      state.selectedUserProfile = action.payload;
    },

    // CREATE USER
    createUserSuccess(state, action) {
      console.debug('user successfully created');
      state.isLoading = false;
      // state.selectedUserProfile = action.payload;
    },

    // GET USERS
    getUsersSuccess(state, action) {
      state.isLoading = false;
      state.users = action.payload;
    },

    // GET USER PERMISSIONS
    getUserPermissionsSuccess(state, action) {
      console.debug('applying user permissions');
      state.isLoading = false;
      state.userPermissions = action.payload;
    },

    // GET MANAGE USERS
    getUserListSuccess(state, action) {
      state.isLoading = false;
      state.userList = action.payload;
      state.enabledUserList = action.payload?.filter((user) => user?.Enabled);
      localStorage.setItem(
        'userMapping',
        JSON.stringify(
          state.userList.map((user) => ({ id: user.id, name: user.name }))
        )
      );
    },

    // GET CARDS
    getCardsSuccess(state, action) {
      state.isLoading = false;
      state.cards = action.payload;
    },

    // GET NOTIFICATIONS
    getNotificationsSuccess(state, action) {
      state.isLoading = false;
      state.notifications = action.payload;
    },

    resetPasswordSuccess(state, action) {
      state.isLoading = false;
      // state.notifications = action.payload;
    },

    hasResetPasswordSuccess(state, action) {
      state.isLoading = false;
      state.isResetPasswordSuccess = action.payload;
    },

    hasErrorResetPassword(state, action) {
      state.isLoading = false;
      state.isResetPasswordError = action.payload;
      state.resetPasswordTrigger += 1;
    },

    changePermissionsSuccess(state, action) {
      state.isLoading = false;
      // state.notifications = action.payload;
    },

    updateAdminProfileSuccess(state, action) {
      state.isLoading = false;
    },

    disableUserSuccess(state, action) {
      const updatedList = Object.values(
        JSON.parse(JSON.stringify(state.userList))
      ).map((user) =>
        user.Username === JSON.parse(action.payload)
          ? { ...user, Enabled: false }
          : user
      );

      state.userList = updatedList;
      state.enabledUserList = updatedList?.filter((user) => user?.Enabled);

      // Update Selected User
      const updatedUser = { ...state.selectedUserProfile, enabled: false };

      state.selectedUserProfile = updatedUser;

      state.isLoading = false;
    },
    enableUserSuccess(state, action) {
      // Update List View
      const updatedList = Object.values(
        JSON.parse(JSON.stringify(state.userList))
      ).map((user) =>
        user.Username === JSON.parse(action.payload)
          ? { ...user, Enabled: true }
          : user
      );

      state.userList = updatedList;
      state.enabledUserList = updatedList?.filter((user) => user?.Enabled);

      // Update Selected User
      // const updatedUser = JSON.parse(JSON.stringify(state.selectedUserProfile));
      const updatedUser = { ...state.selectedUserProfile, enabled: true };

      state.selectedUserProfile = updatedUser;

      state.isLoading = false;
    },

    clearUserDataSuccess(state) {
      state.selectedUserProfile = initialState.selectedUserProfile;
      state.userPermissions = initialState.userPermissions;
    },

    forgotPasswordSuccess(state, action) {
      console.debug(action.payload);
    },

    hasConfirmPassword(state, action) {
      console.debug(action.payload);
      state.isLoading = false;
      state.confirmForgotPasswordSuccess = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { onToggleFollow } = slice.actions;
export const { hasError, clearError } = slice.actions;

// ----------------------------------------------------------------------

export function clearUserData() {
  return async (dispatch) => {
    dispatch(slice.actions.clearUserDataSuccess());
  };
}

// ----------------------------------------------------------------------
export function getProfile() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query getUserProfile {
        getUserProfile {
            id
            displayName 
            email
            firstName
            lastName
            photoURL
            phoneNumber
            country
            address
            state
            city
            zipCode
            about
            role
            company
            isPublic
        }
      }`;
      const response = await axios.post('/graphql', { query: queryString });
      dispatch(
        slice.actions.getProfileSuccess(response.data.data.getUserProfile)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getAdminProfile(username) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query {
        getAdminUserProfile(username:"${username}") {
            id
            displayName 
            email
            firstName
            lastName
            photoURL
            phoneNumber
            country
            address
            state
            city
            zipCode
            about
            role
            company
            enabled
            isPublic
        }
      }`;
      const response = await axios.post('/graphql', { query: queryString });
      dispatch(
        slice.actions.getAdminProfileSuccess(
          response.data.data.getAdminUserProfile
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getUserList() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query {
        getUserList {       
          Username,  
          email,
          UserCreateDate,
          UserLastModifiedDate,
          Enabled,
          UserStatus,
          id,
          name,
          role,
          status,
          company,
          avatarUrl,
          isVerified
        } 
      }`;

      const response = await axios.post('/graphql', { query: queryString });
      dispatch(
        slice.actions.getUserListSuccess(response.data.data.getUserList)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

// export function getCards() {
//   return async (dispatch) => {
//     dispatch(slice.actions.startLoading());
//     try {
//       const response = await axios.get('/api/user/account/cards');
//       dispatch(slice.actions.getCardsSuccess(response.data.cards));
//     } catch (error) {
//       dispatch(slice.actions.hasError(error));
//     }
//   };
// }

// ----------------------------------------------------------------------

export function getNotifications() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        '/api/user/account/notifications-settings'
      );
      dispatch(
        slice.actions.getNotificationsSuccess(response.data.notifications)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getUsers() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/user/all');
      dispatch(slice.actions.getUsersSuccess(response.data.users));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getUserPermissions(username) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const selectedUsername = JSON.stringify(username);
      console.debug('getting permissions for: ', selectedUsername);
      const queryString = `query {
        getUserPermissions(username:${selectedUsername})
           {
              brands,
              branches,
              role,
              worldview
           }
      }`;
      const response = await axios.post('/graphql', { query: queryString });
      dispatch(
        slice.actions.getUserPermissionsSuccess(
          parsePermissions(username, response.data.data.getUserPermissions)
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export async function adminResetPassword(username, password) {
  console.debug('sending reset password request');

  username = JSON.stringify(username);
  password = JSON.stringify(password);

  const queryString = `query {
      adminResetPassword(username:${username}, password:${password})
    }`;
  const response = await axios.post('/graphql', { query: queryString });

  if (response.data.errors) {
    slice.actions.hasError(response.data.errors[0].message);
    throw new Error(response.data.errors[0].message);
  }

  slice.actions.resetPasswordSuccess(response.data);
}

// ----------------------------------------------------------------------

export function resetPassword(username, oldPassword, newPassword) {
  return async (dispatch) => {
    try {
      console.debug('sending reset password request');

      oldPassword = JSON.stringify(oldPassword);
      newPassword = JSON.stringify(newPassword);

      const queryString = `query {
      resetPassword(oldPassword:${oldPassword}, newPassword:${newPassword})
    }`;
      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors) {
        throw new Error(response?.data?.errors[0]?.message);
      }
      dispatch(slice.actions.hasResetPasswordSuccess(true));
      dispatch(slice.actions.hasErrorResetPassword(false));
    } catch (error) {
      dispatch(slice.actions.hasErrorResetPassword(error?.message));
      dispatch(slice.actions.hasResetPasswordSuccess(false));
      throw new Error(error);
    }
  };
}

export function disableUser(username) {
  return async (dispatch) => {
    try {
      console.debug(`disable user ${username}`);
      username = JSON.stringify(username);
      const queryString = `query {
      disableUser(username:${username})
    }`;
      const response = await axios.post('/graphql', { query: queryString });
      dispatch(slice.actions.disableUserSuccess(username));
    } catch (error) {
      slice.actions.hasError(error);
    }
  };
}

export function enableUser(username) {
  return async (dispatch) => {
    try {
      console.debug(`enable user ${username}`);
      username = JSON.stringify(username);
      const queryString = `query {
      enableUser(username:${username})
    }`;
      const response = await axios.post('/graphql', { query: queryString });
      dispatch(slice.actions.enableUserSuccess(username));
    } catch (error) {
      slice.actions.hasError(error);
    }
  };
}

export async function changePermissions(
  username,
  role,
  brands,
  branches,
  worldview
) {
  try {
    console.debug('sending change attribute request');

    username = JSON.stringify(username);
    role = JSON.stringify(role);
    brands = JSON.stringify(brands);
    branches = JSON.stringify(branches);

    const queryString = `query {
      updateUserPermissions(username:${username}, role:${role}, brands:${JSON.stringify(
      brands
    )}, branches:${JSON.stringify(branches)}, worldview:${worldview || false})
    }`;

    const response = await axios.post('/graphql', { query: queryString });

    if (response.data.errors) {
      slice.actions.hasError(response.data.errors[0]);
      throw new Error(response.data.errors[0].message);
    }

    slice.actions.changePermissionsSuccess(response.data);
  } catch (error) {
    slice.actions.hasError(error);
    throw new Error(error);
  }
}

export function createUser({
  email,
  password,
  firstName,
  lastName,
  company,
  role,
  brands,
  branches,
  worldview
}) {
  email = JSON.stringify(email);
  password = JSON.stringify(password);
  firstName = JSON.stringify(firstName);
  lastName = JSON.stringify(lastName);
  company = JSON.stringify(company);
  role = JSON.stringify(role);
  brands = JSON.stringify(brands);
  branches = JSON.stringify(branches);

  const queryString = `query {
    adminCreateUser(email:${email}, password:${password}, firstName:${firstName}, lastName:${lastName}, company:${company}, role:${role}, brands:${JSON.stringify(
    brands
  )}, branches:${JSON.stringify(branches)}, worldview:${worldview || false})
  }`;

  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const response = await axios.post('/graphql', { query: queryString });

    if (response.data.errors && response.data.errors.length > 0) {
      // Extract the first error message and throw it
      const errorMessage = response.data.errors[0].message;
      throw new Error(errorMessage);
    }

    dispatch(slice.actions.createUserSuccess(response.data));
  };
}

// ----------------------------------------------------------------------

export async function updateAdminProfile(
  username,
  phoneNumber,
  firstName,
  lastName,
  company
) {
  try {
    console.debug('sending request to update selected user');

    const queryString = `query {
      updateUserAttributes(username: "${username}", phoneNumber: "${phoneNumber}", firstName: "${firstName}", lastName: "${lastName}", company: "${company}")
     }`;
    const response = await axios.post('/graphql', { query: queryString });

    if (response.data.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      throw new Error(errorMessage);
    }

    slice.actions.updateAdminProfileSuccess(response.data);
  } catch (error) {
    slice.actions.hasError(error);
  }
}

// ----------------------------------------------------------------------

export function forgotPassword(username) {
  console.debug(`Sending forgot password email to ${username}`);
  return async (dispatch) => {
    try {
      const queryString = `query {
        forgotPassword(username:${JSON.stringify(username)})
      }`;

      const response = await axios.post('/graphql', { query: queryString });
      dispatch(slice.actions.forgotPasswordSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function confirmForgotPassword(username, code, newPassword) {
  console.debug(`Confirming forgot password for ${username}`);
  return async (dispatch) => {
    try {
      const queryString = `query {
      confirmForgotPassword(username: ${JSON.stringify(
        username
      )}, code: ${JSON.stringify(code)}, newPassword:${JSON.stringify(
        newPassword
      )})
    }`;

      const response = await axios.post('/graphql', { query: queryString });
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      }
      dispatch(slice.actions.hasConfirmPassword(true));
      dispatch(slice.actions.hasError(false));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(slice.actions.hasConfirmPassword(false));
    }
  };
}
